import { useState, useLayoutEffect, ReactNode } from 'react';
import { useUser } from '@/hooks/user/useUser';
import { isAccountSetupIncomplete } from '@/helpers/profileHelpers';
import { ThemeContext, Theme } from './ThemeContext';

const THEME_STORAGE_KEY = 'theme-preference';

const getInitialTheme = (): Theme => {
    if (typeof window === 'undefined') return 'light';

    try {
        const stored = localStorage.getItem(THEME_STORAGE_KEY);
        if (stored === 'light' || stored === 'dark') {
            return stored;
        }
    }
    catch {
        // localStorage not available (private browsing, etc.)
    }

    return 'light'; // default
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
    const { user } = useUser();

    const initialTheme = getInitialTheme();
    const [theme, setThemeState] = useState<Theme>(initialTheme);

    // Public and get-started flows always use light theme; onboarded users use stored preference
    const useLightThemeOnly = !user || isAccountSetupIncomplete(user);
    const effectiveTheme = useLightThemeOnly ? 'light' : theme;
    const canChangeTheme = Boolean(user) && !isAccountSetupIncomplete(user);

    // Use useLayoutEffect to ensure DOM updates happen synchronously before paint
    useLayoutEffect(() => {
        const root = document.documentElement;

        root.classList.remove('light', 'dark');
        root.classList.add(effectiveTheme);

        // Persist preference only for onboarded users (not during get-started)
        if (canChangeTheme) {
            try {
                localStorage.setItem(THEME_STORAGE_KEY, theme);
            }
            catch {
                // localStorage not available, ignore
            }
        }
    }, [canChangeTheme, effectiveTheme, theme]);

    const setTheme = (newTheme: Theme) => {
        if (!canChangeTheme) {
            return;
        }
        setThemeState(newTheme);
    };

    const toggleTheme = () => {
        if (!canChangeTheme) {
            return;
        }
        setThemeState(current => (current === 'light' ? 'dark' : 'light'));
    };

    return (
        <ThemeContext.Provider
            value={{
                theme: effectiveTheme,
                setTheme,
                toggleTheme,
            }}
        >
            {children}
        </ThemeContext.Provider>
    );
};
